import { forwardRef } from "react";

import { Sentiment as SentimentType } from "@/constants";
import { cx } from "@/utils";
import { SENTIMENT_OPTIONS } from "@/utils/sentimentOptions";
import {
  Dropdown,
  DropdownHeading,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "@components/Dropdown";
import TextButton, { TextButtonProps } from "@components/TextButton";
import { i18n } from "@lingui/core";

import styles from "./Sentiment.module.css";

type SentimentProps = Omit<TextButtonProps, "onChange"> & {
  className?: string;
  isProcessing?: boolean;
  value: SentimentType | null;
  onChange?: (sentiment: SentimentType) => void;
  disabled?: boolean;
};

type SentimentOptionsProps = Pick<SentimentProps, "onChange" | "value">;

export const SentimentOptions: React.FC<SentimentOptionsProps> = ({
  onChange,
  value: currentValue,
}) => {
  return SENTIMENT_OPTIONS.map(({ label, icon: Icon, value }) => (
    <DropdownItem
      key={value}
      onClick={() => {
        onChange?.(value);
      }}
      disabled={value === currentValue}
      className={cx({
        [styles.active]: value === currentValue,
      })}
      data-testid={`sentiment-${value}`}
    >
      <Icon height={18} width={18} /> {i18n._(label)}
    </DropdownItem>
  ));
};

const Sentiment = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  SentimentProps
>(function Sentiment(
  {
    className,
    isProcessing,
    value,
    disabled: passedDisabled,
    onChange,
    ...rest
  },
  ref
) {
  const CURRENT_SENTIMENT = SENTIMENT_OPTIONS.find((s) => s.value === value);

  const disabled = !onChange || !value || passedDisabled;

  if (value == null || !CURRENT_SENTIMENT) {
    return null;
  }

  if (isProcessing) {
    return (
      <TextButton
        {...rest}
        disabled
        className={cx(styles.indicator, className)}
      >
        {L.MENTIONS_SENTIMENT_TONE_PROCESSING}
      </TextButton>
    );
  }

  const inner = (
    <TextButton
      {...rest}
      ref={ref}
      aria-disabled={disabled}
      disabled={disabled}
      className={cx(styles.indicator, className)}
      icon={CURRENT_SENTIMENT?.icon}
    >
      {i18n._(CURRENT_SENTIMENT?.label || "")}
    </TextButton>
  );

  if (!onChange) {
    return inner;
  }

  return (
    <Dropdown>
      <DropdownToggle data-testid="sentiment-indicator" disabled={disabled}>
        {inner}
      </DropdownToggle>
      <DropdownMenu
        data-testid="sentiment-picker"
        onCloseAutoFocus={(e) => {
          // This prevents the focus returning to the clicked button.
          // If this is not done and a tooltip is open, the tooltip will open again after
          // the sentiment has been changed.
          e.preventDefault();
        }}
      >
        <DropdownHeading>{L.MENTIONS_SENTIMENT_EDIT_TONE}</DropdownHeading>
        <SentimentOptions value={value} onChange={onChange} />
      </DropdownMenu>
    </Dropdown>
  );
});

export default Sentiment;
